import axios from "axios";

// Same-origin fallback: when REACT_APP_BACKEND_URL is empty (self-host default
// where SPA + backend live on the same domain behind nginx), use relative
// "/api/v1" so no rebuild is needed if the deployer just wants to serve
// everything off one domain.
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
export const API_BASE = BACKEND_URL ? `${BACKEND_URL}/api/v1` : "/api/v1";

const api = axios.create({
    baseURL: API_BASE,
    headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem("ddi_token");
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

api.interceptors.response.use(
    (r) => r,
    (err) => {
        if (err?.response?.status === 401) {
            const path = window.location.pathname;
            const isPublic =
                path.startsWith("/login") ||
                path.startsWith("/admin/login") ||
                path.startsWith("/super-admin/login") ||
                path.startsWith("/signup") ||
                path.startsWith("/verify-otp") ||
                path.startsWith("/forgot-password");
            if (!isPublic) {
                localStorage.removeItem("ddi_token");
                localStorage.removeItem("ddi_user");
            }
        }
        return Promise.reject(err);
    }
);

export default api;

export function setSession(token, user) {
    localStorage.setItem("ddi_token", token);
    localStorage.setItem("ddi_user", JSON.stringify(user));
}

export function clearSession() {
    localStorage.removeItem("ddi_token");
    localStorage.removeItem("ddi_user");
}

export function getUser() {
    const raw = localStorage.getItem("ddi_user");
    return raw ? JSON.parse(raw) : null;
}

export function getToken() {
    return localStorage.getItem("ddi_token");
}

/**
 * Safely coerce a backend error into a string suitable for toast.error().
 * FastAPI can return `detail` in three shapes:
 *   1. plain string           — passthrough
 *   2. object  {code, message, errors[]}  — our custom app-level errors
 *   3. ARRAY   [{loc:[...], msg, type}]   — Pydantic validation 422s
 *
 * Passing shape (3) straight into a React child or axios' fallback
 * surfaces either "Request failed with status code 422" or React
 * error #130 ("Objects are not valid as a React child"). This helper
 * collapses every shape to a readable string with field prefixes.
 */
export function errMsg(err, fallback = "Something went wrong") {
    const detail = err?.response?.data?.detail;
    if (typeof detail === "string") return detail;
    // Pydantic 422 — array of {loc:["body","field"], msg, type}
    if (Array.isArray(detail) && detail.length) {
        const parts = detail
            .map((e) => {
                if (typeof e === "string") return e;
                const field = Array.isArray(e?.loc)
                    ? e.loc.filter((p) => p !== "body" && typeof p === "string").slice(-1)[0]
                    : null;
                const msg = e?.msg || e?.message || "invalid value";
                const niceField = _prettyField(field);
                return niceField ? `${niceField}: ${msg}` : msg;
            })
            .filter(Boolean);
        if (parts.length) return parts.join(" · ");
    }
    if (detail && typeof detail === "object") {
        if (typeof detail.message === "string") return detail.message;
        if (Array.isArray(detail.errors) && detail.errors.length) {
            return detail.errors
                .map((e) => (typeof e === "string" ? e : e?.msg || e?.label || e?.key))
                .filter(Boolean)
                .join(" · ");
        }
        if (typeof detail.code === "string") return detail.code.replace(/_/g, " ");
    }
    // Last-resort: never leak axios's raw "Request failed with status code N".
    if (err?.response?.status === 422) return "Please check the highlighted fields.";
    if (typeof err?.message === "string" &&
        !/^Request failed with status code \d+$/.test(err.message)) {
        return err.message;
    }
    return fallback;
}


/**
 * Parse a FastAPI 422 response into {field: friendly_msg} for inline
 * form rendering. Returns {} when the error isn't a Pydantic 422 so
 * callers can safely spread it into state.
 */
export function fieldErrors(err) {
    const detail = err?.response?.data?.detail;
    if (!Array.isArray(detail)) return {};
    const out = {};
    for (const e of detail) {
        if (!e || typeof e !== "object") continue;
        const loc = Array.isArray(e.loc)
            ? e.loc.filter((p) => p !== "body" && typeof p === "string")
            : [];
        const field = loc[loc.length - 1];
        if (!field || out[field]) continue;
        out[field] = _humanizePydanticMsg(e.msg || e.message || "Invalid value", e.ctx);
    }
    return out;
}


function _humanizePydanticMsg(msg, ctx) {
    // "String should have at least N characters" → short + warm.
    const minLen = ctx?.min_length;
    const maxLen = ctx?.max_length;
    if (/at least \d+ characters?/i.test(msg) && minLen) {
        return `Please enter at least ${minLen} characters`;
    }
    if (/at most \d+ characters?/i.test(msg) && maxLen) {
        return `Please keep this under ${maxLen} characters`;
    }
    if (/field required/i.test(msg)) return "This field is required";
    if (/value is not a valid email/i.test(msg)) return "Please enter a valid email";
    if (/^Input should be/i.test(msg)) return msg.replace(/^Input should be/, "Must be");
    return msg;
}


function _prettyField(name) {
    if (!name) return null;
    return name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
